import { Link, NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reportsApi } from '@/api/endpoints'
import Icon from '@/components/common/Icons'
import { PERMS, can, isSuperAdmin, roleLabel, useAuth } from '@/store/auth'
import { useBranding } from '@/lib/useBranding'

// Grouped by what someone is doing rather than by how the API is arranged:
// the day's work first, then the catalog behind it, then the numbers, then
// the things an admin sets up once. `requires` hides an entry from a role
// that would only get a 403 from it.
// Hidden, not removed. The test bench page and its route are untouched —
// it is simply not offered in the sidebar. Flip this back to true to list
// it again.
const SHOW_TEST_BENCH = false

const NAV = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: 'dashboard', end: true, requires: PERMS.dashboardRead },
      // Lands on the plain list, which opens on today's orders — the day's
      // work is what this link is for, and it used to arrive pre-filtered
      // to 'needs review' instead.
      //
      // No badge. It counted orders needing review from any day, so once
      // this link stopped going to that filtered list the number sat above
      // a list that did not contain the things it was counting. 'Needs
      // review' in the status dropdown still finds them, worst-confidence
      // first. Adding `badge: 'pending_review'` back is all it would take.
      { to: '/orders', label: 'Orders', icon: 'orders', requires: PERMS.ordersRead },
      // No badge. It counted every message with status FAILED, which is
      // almost entirely unregistered senders — rows the queue never retries
      // by design, so the number only ever went up and nothing an operator
      // did on this page brought it down. Those conversations are listed on
      // the page itself. Adding `badge: 'failed_messages'` back is all it
      // would take.
      { to: '/messages', label: 'Messages', icon: 'messages', requires: PERMS.messagesRead },
      { to: '/customers', label: 'Customers', icon: 'customers', requires: PERMS.customersRead },
      {
        to: '/transport-services',
        label: 'Transport services',
        icon: 'transport',
        requires: PERMS.deliveriesRead,
      },
      // What sold, by product, customer and category, for a chosen period.
      {
        to: '/sales-reports',
        label: 'Sales Reports',
        icon: 'orders',
        requires: PERMS.reportsRead,
      },
      // Confirmed and packed orders by transporter, to book the loads from.
      {
        to: '/transport-loads',
        label: 'Transport Reports',
        icon: 'transport',
        requires: PERMS.transportLoadsRead,
      },
    ],
  },
  {
    section: 'Catalog',
    items: [
      {
        to: '/products',
        label: 'Products',
        icon: 'products',
        end: true,
        requires: PERMS.productsRead,
        // Categories and units describe the catalog, so they live under it
        // rather than competing with it for a top-level row.
        children: [
          { to: '/products/categories', label: 'Categories', icon: 'categories', requires: PERMS.productsWrite },
          { to: '/products/units', label: 'Units', icon: 'units', requires: PERMS.productsWrite },
          { to: '/products/unit-weights', label: 'Unit weights', icon: 'weights', requires: PERMS.productsWrite },
        ],
      },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/users', label: 'Users', icon: 'users', requires: PERMS.usersRead },
      { to: '/roles', label: 'Roles', icon: 'roles', requires: PERMS.rolesRead },
      // Spend and provider keys are an administrator's business, not a
      // reviewer's, so these sit with the other things only an admin opens
      // rather than in a section of their own.
      { to: '/ai-activity', label: 'AI activity', icon: 'ai', requires: PERMS.aiRead },
      {
        to: '/test-bench', label: 'Test bench', icon: 'testbench',
        requires: PERMS.playgroundRun, hidden: !SHOW_TEST_BENCH,
      },
      // Only for a role that can change settings: a packer or a viewer has
      // nothing to do on a screen of provider keys and company details.
      { to: '/settings', label: 'Settings', icon: 'settings', requires: PERMS.settingsWrite },
    ],
  },
]

export default function Layout() {
  const { user, logout } = useAuth()
  const { siteLogo } = useBranding()

  // Drives the sidebar badges; polled so a reviewer sees new work arrive.
  const { data: stats } = useQuery({
    queryKey: ['dashboard'],
    queryFn: reportsApi.dashboard,
    refetchInterval: 30_000,
  })

  // A super admin holds every permission, including ones added after this
  // build shipped, so it never needs to appear in the list explicitly.
  // `hidden` is checked before the permission, and deliberately not
  // expressed as one: a super admin holds every permission and would still
  // see an entry we have chosen to take off the menu.
  const allowed = (item) =>
    !item.hidden && (!item.requires || can(user, item.requires) || isSuperAdmin(user))

  const link = (item, isChild = false) => {
    const count = item.badge ? stats?.[item.badge] : 0
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          `nav-item${isChild ? ' nav-item-child' : ''}${isActive ? ' active' : ''}`
        }
      >
        <Icon name={item.icon} className="nav-icon" />
        <span className="nav-label">{item.label}</span>
        {count > 0 && <span className="nav-badge">{count}</span>}
      </NavLink>
    )
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        {/* The mark is the way home, as it is on every site — so the
            dashboard is one click from anywhere without spending a nav row. */}
        <Link to="/" className="brand" aria-label="MetroMart — go to the dashboard">
          {/* Empty, same size, until the branding is known: never the old logo. */}
          {siteLogo ? (
            <img src={siteLogo} alt="MetroMart" className="brand-logo" />
          ) : (
            <span className="brand-logo logo-placeholder" aria-hidden="true" />
          )}
        </Link>

        <nav className="nav">
          {NAV.map((group, index) => {
            const items = group.items.filter((item) => allowed(item))
            if (!items.length) return null
            return (
              <div className="nav-group" key={group.section ?? index}>
                {group.section && <div className="nav-section">{group.section}</div>}
                {items.map((item) => (
                  <div key={item.to}>
                    {link(item)}
                    {(item.children ?? []).filter(allowed).map((child) => link(child, true))}
                  </div>
                ))}
              </div>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <Link to="/account" className="user-chip" title="Your account and password">
            <div className="user-name">{user?.full_name}</div>
            <div className="user-role">{roleLabel(user)}</div>
          </Link>
          <button type="button" className="btn btn-ghost btn-sm" onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  )
}
